import React, { useState, useEffect } from 'react';
import { Asset, AssetReview, assetAPI } from '../../services/assetAPI';
import {
  X,
  Edit2,
  Archive,
  Server,
  Lock,
  Unlock,
  HardDrive,
  MapPin,
  User,
  Calendar,
  Shield,
  Clock,
  FileText,
  CheckCircle,
  XCircle,
  Plus,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

interface AssetDetailProps {
  asset: Asset;
  onClose: () => void;
  onEdit: () => void;
  onArchive: (reason: string) => void;
  onRefresh: () => void;
}

const AssetDetail: React.FC<AssetDetailProps> = ({
  asset,
  onClose,
  onEdit,
  onArchive,
  onRefresh
}) => {
  const [reviews, setReviews] = useState<AssetReview[]>([]);
  const [isLoadingReviews, setIsLoadingReviews] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [archiveReason, setArchiveReason] = useState('');
  const [expandedSections, setExpandedSections] = useState<string[]>(['overview', 'security', 'reviews']);

  useEffect(() => {
    loadReviews();
  }, [asset.id]);

  const loadReviews = async () => {
    setIsLoadingReviews(true);
    try {
      const data = await assetAPI.getReviews(asset.id);
      setReviews(data);
    } catch (error) {
      console.error('Failed to load reviews:', error);
    } finally {
      setIsLoadingReviews(false);
    }
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev =>
      prev.includes(section)
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };

  const handleArchive = () => {
    if (archiveReason.trim()) {
      onArchive(archiveReason);
    }
  };

  const getClassificationColor = (classification: string) => {
    switch (classification) {
      case 'critical': return 'bg-red-500';
      case 'important': return 'bg-amber-500';
      case 'low_risk': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  const getReviewStatusColor = (status: string) => {
    switch (status) {
      case 'current': return 'text-green-600 bg-green-50';
      case 'due_soon': return 'text-amber-600 bg-amber-50';
      case 'overdue': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className={`${getClassificationColor(asset.classification)} px-6 py-4`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Server size={24} className="text-white" />
              <div>
                <h2 className="text-xl font-semibold text-white">{asset.asset_name}</h2>
                <p className="text-white/80 text-sm capitalize">
                  {asset.asset_type.replace('_', ' ')} | {asset.classification.replace('_', ' ')}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={onEdit}
                className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg"
                title="Edit Asset"
              >
                <Edit2 size={20} />
              </button>
              <button
                onClick={() => setShowArchiveConfirm(true)}
                className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg"
                title="Archive Asset"
              >
                <Archive size={20} />
              </button>
              <button
                onClick={onClose}
                className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-80px)] p-6">
          {/* Overview Section */}
          <div className="mb-6">
            <button
              onClick={() => toggleSection('overview')}
              className="flex items-center justify-between w-full text-left"
            >
              <h3 className="text-lg font-semibold text-gray-900">Overview</h3>
              {expandedSections.includes('overview') ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>
            {expandedSections.includes('overview') && (
              <div className="mt-4 grid grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin size={16} className="text-gray-400" />
                    <span className="text-gray-500">Location:</span>
                    <span className="text-gray-900">{asset.location || 'Not specified'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <User size={16} className="text-gray-400" />
                    <span className="text-gray-500">Owner:</span>
                    <span className="text-gray-900">{asset.owner_name || 'Unassigned'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar size={16} className="text-gray-400" />
                    <span className="text-gray-500">Last Review:</span>
                    <span className="text-gray-900">{asset.last_review_date || 'Never'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Clock size={16} className="text-gray-400" />
                    <span className="text-gray-500">Next Review:</span>
                    <span className={`px-2 py-0.5 rounded text-xs ${getReviewStatusColor(asset.review_status)}`}>
                      {asset.next_review_date || 'Not scheduled'}
                    </span>
                  </div>
                </div>
                <div className="space-y-3">
                  {asset.description && (
                    <div>
                      <span className="text-sm text-gray-500 block mb-1">Description</span>
                      <p className="text-sm text-gray-900">{asset.description}</p>
                    </div>
                  )}
                  {asset.data_stored && (
                    <div>
                      <span className="text-sm text-gray-500 block mb-1">Data Stored</span>
                      <p className="text-sm text-gray-900">{asset.data_stored}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Security Section */}
          <div className="mb-6 border-t pt-6">
            <button
              onClick={() => toggleSection('security')}
              className="flex items-center justify-between w-full text-left"
            >
              <h3 className="text-lg font-semibold text-gray-900">Security & Compliance</h3>
              {expandedSections.includes('security') ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>
            {expandedSections.includes('security') && (
              <div className="mt-4 grid grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      {asset.encryption_status ? (
                        <Lock size={18} className="text-green-500" />
                      ) : (
                        <Unlock size={18} className="text-red-500" />
                      )}
                      <span className="text-sm font-medium">Encryption</span>
                    </div>
                    <span className={`text-sm ${asset.encryption_status ? 'text-green-600' : 'text-red-600'}`}>
                      {asset.encryption_status ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                  {asset.encryption_details && (
                    <p className="text-sm text-gray-600 px-3">{asset.encryption_details}</p>
                  )}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <HardDrive size={18} className={asset.backup_status ? 'text-green-500' : 'text-gray-400'} />
                      <span className="text-sm font-medium">Backup</span>
                    </div>
                    <span className={`text-sm ${asset.backup_status ? 'text-green-600' : 'text-gray-500'}`}>
                      {asset.backup_status ? 'Configured' : 'Not Configured'}
                    </span>
                  </div>
                  {asset.backup_frequency && (
                    <p className="text-sm text-gray-600 px-3">
                      Frequency: {asset.backup_frequency} | Retention: {asset.backup_retention || 'N/A'}
                    </p>
                  )}
                </div>
                <div className="space-y-4">
                  {asset.access_controls && (
                    <div>
                      <span className="text-sm text-gray-500 block mb-1">Access Controls</span>
                      <p className="text-sm text-gray-900">{asset.access_controls}</p>
                    </div>
                  )}
                  {asset.compliance_requirements && asset.compliance_requirements.length > 0 && (
                    <div>
                      <span className="text-sm text-gray-500 block mb-1">Compliance Requirements</span>
                      <div className="flex flex-wrap gap-1">
                        {asset.compliance_requirements.map((req, idx) => (
                          <span key={idx} className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">
                            {req}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Reviews Section */}
          <div className="mb-6 border-t pt-6">
            <button
              onClick={() => toggleSection('reviews')}
              className="flex items-center justify-between w-full text-left"
            >
              <h3 className="text-lg font-semibold text-gray-900">Review History</h3>
              {expandedSections.includes('reviews') ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>
            {expandedSections.includes('reviews') && (
              <div className="mt-4">
                {isLoadingReviews ? (
                  <div className="text-center py-4 text-gray-500">Loading reviews...</div>
                ) : reviews.length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 rounded-lg">
                    <FileText size={32} className="mx-auto mb-2 text-gray-300" />
                    <p className="text-sm text-gray-500">No reviews yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {reviews.map((review) => (
                      <div key={review.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                        <div className={`w-2 h-2 mt-2 rounded-full ${
                          review.status === 'completed' ? 'bg-green-500' :
                          review.status === 'in_progress' ? 'bg-blue-500' : 'bg-gray-400'
                        }`} />
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-900 capitalize">
                              {review.review_type.replace('_', ' ')} Review
                            </span>
                            <span className="text-xs text-gray-500">{review.review_date}</span>
                          </div>
                          {review.reviewer_name && (
                            <p className="text-xs text-gray-500 mt-0.5">by {review.reviewer_name}</p>
                          )}
                          {review.findings && (
                            <p className="text-sm text-gray-600 mt-2">{review.findings}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Special Notes */}
          {asset.special_notes && (
            <div className="mb-6 border-t pt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Special Notes</h3>
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">{asset.special_notes}</p>
              </div>
            </div>
          )}
        </div>

        {/* Archive Confirmation Modal */}
        {showArchiveConfirm && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Archive Asset</h3>
              <p className="text-sm text-gray-500 mb-4">
                Are you sure you want to archive "{asset.asset_name}"? This will remove it from the active asset list.
              </p>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason for archiving
                </label>
                <textarea
                  value={archiveReason}
                  onChange={(e) => setArchiveReason(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  rows={3}
                  placeholder="Enter reason..."
                />
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowArchiveConfirm(false);
                    setArchiveReason('');
                  }}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
                >
                  Cancel
                </button>
                <button
                  onClick={handleArchive}
                  disabled={!archiveReason.trim()}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 disabled:opacity-50"
                >
                  Archive Asset
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssetDetail;
